import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import type { SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { isNullsy } from '~/_shared/utils/typeGuards';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { type DataType } from '~/store/spreadsheetData/spreadsheetData.state';
import { createCancelToken } from '../api/api.helpers';
import {
  getSpreadsheetColumnRowValues, type SpreadsheetColumnRowValues,
} from './spreadsheetColumnRowValues.helpers';

export const useSpreadsheetColumnRowValues = <T extends DataType>(
  spreadsheetColumn: SpreadsheetColumnId | null,
  dataType: T,
) => {
  const emptyRowValues = useMemo((): SpreadsheetColumnRowValues<T> => new Map(), []);
  const [rowValues, setRowValues] = useState<SpreadsheetColumnRowValues<T>>(emptyRowValues);
  const [isLoading, setIsLoading] = useState(false);
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const spreadsheetId = spreadsheetColumn?.spreadsheetId;
  const columnId = spreadsheetColumn?.columnId;
  // Caches promises, so re-selecting a column with an in-flight request reuses it.
  const cacheRef = useRef(new Map<string, Promise<SpreadsheetColumnRowValues<T>>>());
  // Guards against a stale request overwriting state for a column selected after it.
  const latestCacheKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (isNullsy(spreadsheetId) || isNullsy(columnId) || isNullsy(clientId) || isNullsy(mapId)) {
      latestCacheKeyRef.current = null;
      setIsLoading(false);
      setRowValues(emptyRowValues);
      return;
    }

    const cacheKey = `${spreadsheetId}-${columnId}-${dataType}`;
    latestCacheKeyRef.current = cacheKey;

    setIsLoading(true);

    let cachedPromise = cacheRef.current.get(cacheKey);
    if (!cachedPromise) {
      cachedPromise = getSpreadsheetColumnRowValues({
        clientId,
        mapId,
        cancelToken: createCancelToken().token,
        spreadsheetColumn: { spreadsheetId, columnId },
        dataType,
      });
      cachedPromise.catch(() => {
        cacheRef.current.delete(cacheKey);
      });
      cacheRef.current.set(cacheKey, cachedPromise);
    }

    cachedPromise
      .then((values) => {
        if (latestCacheKeyRef.current === cacheKey) {
          setRowValues(values);
        }
      })
      .catch(() => {
        if (latestCacheKeyRef.current === cacheKey) {
          setRowValues(emptyRowValues);
        }
      })
      .finally(() => {
        if (latestCacheKeyRef.current === cacheKey) {
          setIsLoading(false);
        }
      });
  }, [spreadsheetId, columnId, clientId, mapId, dataType, emptyRowValues]);

  return {
    rowValues,
    isLoading,
  };
};
